import React, { useState, useEffect } from "react";
import { FaPlus, FaPaw, FaMars, FaVenus, FaCamera } from "react-icons/fa";
import axios from "axios";
import { toast } from "react-toastify";
import { jwtDecode } from "jwt-decode";
import { useLocation } from "react-router-dom";
import BasicinfopetForm from "../../Mapping/components/Basicinfopet/Basicinfopet";
import VaccinesForm from "../../Mapping/components/Basicinfopet/PetsVaccines";
import MedicalHistory from "../../Mapping/components/Basicinfopet/PetsMedicalHistory";
import { PetDrawer } from "../PetDrawer/PetDrawer";
import config from "../../config";

const Petlisting = () => {
  const [activePet, setActivePet] = useState(null);
  const [pets, setPets] = useState([]);

  const [petTypes, setPetTypes] = useState([]);
  const [petBreeds, setPetBreeds] = useState([]);
  const [petSizes, setPetSizes] = useState([]);
  const [activeSection, setActiveSection] = useState("Profile");
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [petToEdit, setPetToEdit] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();
  const token = localStorage.getItem("token");
  const decoded = token ? jwtDecode(token) : null;
  const client_id = decoded?.data?.customer_data?.id;
  const selectedPetId = location.state?.selectedPetId;

  const tabs = ["Profile", "Diary", "Visits"];

  useEffect(() => {
    if (!client_id) return;
    fetchPets();
    fetchPetTypes();
    fetchBreeds();
    fetchSizes();
  }, [client_id]);

  useEffect(() => {
    if (!selectedPetId || pets.length === 0) return;

    const selectedPet = pets.find((pet) => pet._id === selectedPetId);
    if (!selectedPet) return;

    setActivePet(selectedPetId);
    setPetToEdit(selectedPet);
    setActiveSection("Profile");
  }, [selectedPetId, pets]);

  const fetchPets = async () => {
    try {
      setIsLoading(true);
      const res = await axios.post(
        `${config.API_BASE_URL}/customer/getallpet`,
        { client_id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const petList = res.data?.pets || [];
      setPets(petList);
      if (petList.length > 0 && !activePet) setActivePet(petList[0]._id);
    } catch {
      toast.error("Failed to fetch pets");
      setPets([]);
    } finally {
      setIsLoading(false);
    }
    console.log(pets);
    console.log("Pets loaded in Petlisting:", pets.length, pets.map(p => p.name));
  };

  const fetchPetTypes = async () => {
    try {
      const res = await axios.get(
        `${config.API_BASE_URL}/customer/customerpettype`
      );
      const list = Array.isArray(res.data?.data) ? res.data.data : [];
      setPetTypes(list);
    } catch {
      toast.error("Failed to fetch pet types");
      setPetTypes([]);
    }
  };

  const fetchSizes = async () => {
    try {
      const res = await axios.get(
        `${config.API_BASE_URL}/customer/customerpetsize`
      );
      if (res.data?.status) setPetSizes(res.data.data);
    } catch {
      toast.error("Failed to fetch pet sizes");
    }
  };

  const getPetTypeName = (id) => {
    if (!Array.isArray(petTypes)) return "-";
    const type = petTypes.find((t) => t._id === id);
    return type?.name || "-";
  };

  const fetchBreeds = async () => {
    try {
      const res = await axios.get(
        `${config.API_BASE_URL}/customer/customerbreed`
      );
      setPetBreeds(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch {
      toast.error("Failed to fetch pet breeds");
      setPetBreeds([]);
    }
  };

  const getBreedName = (id) => {
    if (!Array.isArray(petBreeds)) return "-";
    return petBreeds.find((b) => b._id === id)?.breed_name || "-";
  };

  const handleProfileImageUpload = async (e) => {
  const file = e.target.files[0];
  if (!file || !currentPet?._id) return;

  // 1️⃣ Show instantly
  const previewUrl = URL.createObjectURL(file);

  setPets((prev) =>
    prev.map((pet) =>
      pet._id === currentPet._id
        ? { ...pet, profile_photo: previewUrl }
        : pet
    )
  );

  try {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("module", "Pet");
    formData.append("docType", "profile_photo");
    formData.append("customerId", client_id);

    const uploadRes = await axios.post(
      `${config.API_BASE_URL}/upload/fileuploadcustomer`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } }
    );

    const uploadedUrl = uploadRes.data?.data?.s3Url;

    // 2️⃣ Replace preview with real URL
    setPets((prev) =>
      prev.map((pet) =>
        pet._id === currentPet._id
          ? { ...pet, profile_photo: uploadedUrl }
          : pet
      )
    );

    await axios.put(
      `${config.API_BASE_URL}/customer/updatepet/${currentPet._id}`,
      { profile_photo: uploadedUrl }
    );

    toast.success("Photo updated");
  } catch (err) {
    toast.error("Failed to update photo");
    fetchPets(); // fallback sync
  }
};
  
const getImageUrl = (photo) => {
  if (!photo) return null;
  if (photo.startsWith("http") || photo.startsWith("blob:"))
    return photo;
  return `${config.S3_BASE_URL || process.env.REACT_APP_S3_BASE_URL}/${photo}`;
};

  const currentPet = pets.find((p) => p._id === activePet);

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-blue-50 overflow-x-hidden">
      <style>{`
        @keyframes slideIn { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes scaleIn { from { opacity: 0; transform: scale(0.9); } to { opacity: 1; transform: scale(1); } }
        .animate-slide-in { animation: slideIn 0.5s ease-out; }
        .animate-fade-in { animation: fadeIn 0.6s ease-out; }
        .animate-scale-in { animation: scaleIn 0.4s ease-out; }
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
        .pet-card-shadow { box-shadow: 0 10px 30px -5px rgba(0, 0, 0, 0.1); }
        .pet-card-shadow:hover { box-shadow: 0 20px 40px -5px rgba(0, 0, 0, 0.15); }
      `}</style>

      <div className="mx-auto w-full max-w-none px-0 py-2 sm:px-4 xl:px-6">
        {/* Header Section */}
        <div className="mb-2 sm:mb-3 animate-slide-in px-4 sm:px-0">
          <div className="flex flex-col gap-6">
            {/* Pet Selection Section */}
            <div className="relative bg-white/60 backdrop-blur-sm rounded-2xl sm:rounded-3xl p-4 sm:p-5 pet-card-shadow">
              <div className="flex items-center justify-between flex-wrap gap-4 
                md:grid md:grid-cols-[1fr_auto] md:items-center
                lg:grid lg:grid-cols-[1fr_auto_1fr]">
                <div className="hidden lg:block" />
               
                  <div className="flex justify-center flex-wrap gap-4 mx-auto max-w-fit">
                    {pets.map((pet) => {
                      const isActive = activePet === pet._id;
                      const hasImage =
                        pet.profile_photo &&
                        (pet.profile_photo.startsWith("http")
                          ? true
                          : Boolean(config.S3_BASE_URL || process.env.REACT_APP_S3_BASE_URL));

                      return (
                        <button
                          key={pet._id}
                          onClick={() => {
                            setActivePet(pet._id);
                            setActiveSection("Profile");
                            setPetToEdit(pet);
                          }}
                          className={`group flex flex-col transition-all duration-300 ${
                            isActive
                              ? "scale-110"
                              : "scale-100 opacity-75 hover:opacity-100 hover:scale-105"
                          }`}
                        >
                          <div className="relative flex justify-center">
                            <div
                              className={`w-10 h-10 sm:w-16 sm:h-16 rounded-full overflow-hidden border-4 shadow-lg flex items-center justify-center text-xl font-bold transition-all duration-300 ${
                                isActive
                                  ? "border-orange-500 ring-2 sm:ring-4 ring-orange-200 scale-105"
                                  : "border-gray-200 group-hover:border-orange-300 opacity-75 group-hover:opacity-100"
                              }`}
                            >
                              {hasImage ? (
                                <img
                                  src={getImageUrl(pet.profile_photo)}
                                  alt={pet.name}
                                  className="w-full h-full object-cover"
                                  loading="lazy"
                                />
                              ) : (
                                <span className="text-gray-700">
                                  {pet.name.charAt(0)}
                                </span>
                              )}
                            </div>

                            {isActive && (
                              <div className="absolute -bottom-1 -right-1 w-4 h-4 sm:w-5 sm:h-5 bg-orange-500 rounded-full flex items-center justify-center border-2 border-white">
                                <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                              </div>
                            )}
                          </div>

                          <p
                            className={`mt-3 text-sm xl:text-base font-bold transition-colors ${
                              isActive
                                ? "text-orange-600"
                                : "text-gray-700 group-hover:text-orange-500"
                            }`}
                          >
                            {pet.name}
                          </p>
                        </button>
                      );
                    })}
                  </div>
                

                <div className="flex justify-end">
                  <button
                    onClick={() => {
                      setPetToEdit(null);
                      setIsDrawerOpen(true);
                    }}
                    className="flex items-center justify-center w-10 h-10 rounded-full 
                    bg-gradient-to-r from-orange-400 via-orange-500 to-orange-500 
                    hover:from-orange-400 hover:via-orange-500 hover:to-orange-600 
                    text-white shadow-lg hover:shadow-2xl hover:-translate-y-1 transition-all
                    lg:w-auto lg:h-auto lg:px-5 lg:py-3 lg:rounded-xl"
                  >
                     {/* Mobile: Only plus icon */}
                <FaPlus className="text-md group-hover:rotate-90 transition-transform duration-300" />
                
                {/* Desktop: Show text */}
                <span className="hidden lg:inline ml-2">Add New Pet</span>
              </button>
                </div>
                
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        {isLoading ? (
        <div className="text-center py-20 px-6 animate-fade-in">
          <div className="inline-flex items-center justify-center w-32 h-32 bg-gradient-to-br from-orange-100 to-orange-200 rounded-full mb-8 shadow-2xl">
            <div className="w-16 h-16 border-4 border-t-orange-500 border-orange-200 rounded-full animate-spin"></div>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-3">
            Loading your pets...
          </h2>
          <p className="text-lg text-gray-600">
            Please wait a moment
          </p>
        </div>
      ) : pets.length > 0 ? (
          <div className="space-y-2 sm:space-y-3 animate-fade-in px-4 sm:px-0">
            {/* Pet Header Card */}
            <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-2xl p-5 sm:p-8 lg:p-10 pet-card-shadow">
              <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 lg:gap-8">
                <div className="relative group flex-shrink-0">
                  <div className="absolute inset-0 bg-gradient-to-br from-orange-400 to-orange-600 rounded-full blur-xl opacity-30 group-hover:opacity-50 transition-opacity"></div>

                  {currentPet.profile_photo ? (
                    <img
                      src={getImageUrl(currentPet.profile_photo)}
                      alt={currentPet.name}
                      className="
                          relative
                          w-20 h-20
                          sm:w-20 sm:h-20
                          md:w-24 md:h-24
                          lg:w-44 lg:h-44
                          object-cover rounded-full
                          border-4 border-white
                          shadow-2xl
                          ring-2 sm:ring-3 lg:ring-4 ring-orange-200
                          group-hover:scale-105 transition-transform
                        "
                      />
                  ) : (
                    <div
                      className="
                        relative
                        w-24 h-24
                        flex items-center justify-center
                        rounded-full
                        border-4 border-white
                        shadow-2xl
                        ring-2 sm:ring-3 lg:ring-4 ring-orange-200
                        bg-gray-200
                        text-3xl sm:text-4xl lg:text-6xl
                        font-bold text-gray-700
                      "
                    >
                      {currentPet.name.charAt(0).toUpperCase()}
                    </div>
                  )}

                  <label className="absolute inset-0 rounded-full bg-gradient-to-br from-black/60 to-black/40 backdrop-blur-sm flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-all cursor-pointer">
                    <FaCamera className="text-2xl sm:text-3xl text-white mb-1" />
                    <span className="text-xs sm:text-sm text-white font-medium">
                      Change Photo
                    </span>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleProfileImageUpload}
                    />
                  </label>
                </div>

                <div className="flex-1 text-center sm:text-left">
                  <div className="flex flex-wrap items-center justify-center sm:justify-start gap-4 mb-6">
                    <h1 className="text-2xl sm:text-2xl lg:text-4xl font-extrabold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                      {currentPet.name}
                    </h1>
                    {currentPet.breed && (
                    <span className="px-4 py-2 bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 rounded-full font-semibold text-sm shadow-md">
                      {getBreedName(currentPet.breed)}
                    </span>
                     )}
                    {currentPet.gender === "male" ? (
                      <span className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-100 to-blue-200 text-blue-700 rounded-full font-semibold text-sm shadow-md">
                        <FaMars className="text-base" />
                        <span className="hidden xs:inline">Male</span>
                        <span className="xs:hidden">M</span>
                      </span>
                    ) : (
                      <span className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-pink-100 to-pink-200 text-pink-700 rounded-full font-semibold text-sm shadow-md">
                        <FaVenus className="text-base" />
                        <span className="hidden xs:inline">Female</span>
                        <span className="xs:hidden">F</span>
                      </span>
                    )}
                  </div>

                  <div className="flex flex-wrap justify-center sm:justify-start gap-3 sm:gap-3 text-gray-600 text-base sm:text-lg">
                    <span className="flex items-center gap-2">
                      <span className="text-2xl">🎂</span>
                      {currentPet.age || "Age not specified"}
                    </span>
                    {currentPet.weight_kg && (
                      <span className="flex items-center gap-3">
                        <span className="text-2xl">⚖️</span>
                        {currentPet.weight_kg} kg
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Tabs */}
              <div className="mt-2 bg-gradient-to-br from-gray-50 to-gray-100 p-2 rounded-2xl shadow-inner">
                <div className="flex flex-wrap gap-3 justify-center sm:justify-start">
                  {tabs.map((tab) => (
                    <button
                      key={tab}
                      onClick={() => setActiveSection(tab)}
                      className={`flex-1 min-w-[80px] px-5 py-3 rounded-xl font-bold transition-all ${
                        activeSection === tab
                          ? "bg-gradient-to-r from-orange-400 to-orange-500 text-white shadow-xl scale-105"
                          : "text-gray-600 hover:text-gray-900 hover:bg-white/70"
                      }`}
                    >
                      {tab}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Tab Content */}
            <div className=" backdrop-blur-sm  shadow-2xl  pet-card-shadow animate-scale-in">
              {activeSection === "Profile" && (
                <BasicinfopetForm petToEdit={currentPet} onSave={fetchPets} />
              )}
              {activeSection === "Diary" && (
                <VaccinesForm pet={currentPet} onSave={fetchPets} />
              )}
              {activeSection === "Visits" && (
                <MedicalHistory pet={currentPet} />
              )}
            </div>
          </div>
        ) : (
          <div className="text-center py-20 px-6 animate-fade-in">
            <div className="inline-flex items-center justify-center w-32 h-32 bg-gradient-to-br from-orange-100 to-orange-200 rounded-full mb-8 shadow-2xl">
              <FaPaw className="text-7xl text-orange-500" />
            </div>
            <h2 className="text-3xl font-bold text-gray-800 mb-4">
              No Pets Yet
            </h2>
            <p className="text-xl text-gray-600 mb-10">
              Start by adding your first furry friend!
            </p>
            <button
              onClick={() => {
                setPetToEdit(null);
                setIsDrawerOpen(true);
              }}
              className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white font-bold rounded-xl shadow-2xl hover:shadow-3xl hover:-translate-y-1 transition-all"
            >
              <FaPlus /> Add Your First Pet
            </button>
          </div>
        )}
      </div>

      <PetDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        onSave={fetchPets}
        petToEdit={petToEdit}
        existingPets={pets}
      />
    </div>
  );
};

export default Petlisting;
